import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './MealDetail.css';
import publicMeals from '../../resources/publicMeals.json';
import { extractMealIdFromSlug, generateMealUrl } from '../../utils/urlHelpers';

const MealDetail = () => {
    const { mealSlug } = useParams();
    const [meal, setMeal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedMeals, setRelatedMeals] = useState([]);
    const [comparisonType, setComparisonType] = useState('calories');
    const [comparisonRange, setComparisonRange] = useState('similar');
    const [comparisonMeals, setComparisonMeals] = useState([]);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        let mealId;
        let foundMeal;

        // Check if the slug contains hyphens (new format) or is just a number (old format)
        if (mealSlug.includes('-')) {
            // New format: extract meal ID from the slug (last part after the last hyphen)
            mealId = extractMealIdFromSlug(mealSlug);
            foundMeal = publicMeals.find(m => m.meal_id === mealId);
        } else {
            // Old format: the slug is just the meal ID
            mealId = mealSlug;
            foundMeal = publicMeals.find(m => m.meal_id === mealId);

            // If found with old format, redirect to new format for SEO
            if (foundMeal && typeof window !== 'undefined') {
                const newUrl = generateMealUrl(foundMeal.name, foundMeal.meal_id);
                if (window.location.pathname !== newUrl) {
                    window.history.replaceState(null, '', newUrl);
                }
            }
        }

        setMeal(foundMeal);
        setLoading(false);

        // Get related meals (same cuisine or similar calories)
        if (foundMeal) {
            const related = publicMeals
                .filter(m => m.meal_id !== mealId)
                .slice(0, 6); // Show 6 related meals
            setRelatedMeals(related);
        }
    }, [mealSlug]);

    // Handle meal comparison
    useEffect(() => {
        if (!meal) return;

        let filteredMeals = publicMeals.filter(m => m.meal_id !== meal.meal_id);

        switch (comparisonType) {
            case 'calories':
                const calorieValue = Number(meal.total_calories);
                if (comparisonRange === 'similar') {
                    // Use smarter range calculation: ±20% but cap at ±50 calories for high-calorie meals
                    let range;
                    if (calorieValue > 300) {
                        // For very high calorie meals, use ±10% but cap at ±50
                        const percentageRange = calorieValue * 0.1;
                        const maxRange = 50;
                        range = Math.min(percentageRange, maxRange);
                    } else {
                        // For normal meals, use ±20% but cap at ±50
                        const percentageRange = calorieValue * 0.2;
                        const maxRange = 50;
                        range = Math.min(percentageRange, maxRange);
                    }
                    filteredMeals = filteredMeals.filter(m => {
                        const mealCalories = Number(m.total_calories);
                        return mealCalories >= calorieValue - range && mealCalories <= calorieValue + range;
                    });
                } else if (comparisonRange === 'lower') {
                    filteredMeals = filteredMeals.filter(m => {
                        const mealCalories = Number(m.total_calories);
                        return mealCalories < calorieValue;
                    });
                } else if (comparisonRange === 'higher') {
                    filteredMeals = filteredMeals.filter(m => {
                        const mealCalories = Number(m.total_calories);
                        return mealCalories > calorieValue;
                    });
                }
                break;

            case 'protein':
                const proteinValue = Number(meal.total_protein);
                if (comparisonRange === 'similar') {
                    // Use smarter range calculation: ±20% but cap at ±5g for high-protein meals
                    const percentageRange = proteinValue * 0.2;
                    const maxRange = 5;
                    const range = Math.min(percentageRange, maxRange);
                    filteredMeals = filteredMeals.filter(m => {
                        const mealProtein = Number(m.total_protein);
                        return mealProtein >= proteinValue - range && mealProtein <= proteinValue + range;
                    });
                } else if (comparisonRange === 'lower') {
                    filteredMeals = filteredMeals.filter(m => {
                        const mealProtein = Number(m.total_protein);
                        return mealProtein < proteinValue;
                    });
                } else if (comparisonRange === 'higher') {
                    filteredMeals = filteredMeals.filter(m => {
                        const mealProtein = Number(m.total_protein);
                        return mealProtein > proteinValue;
                    });
                }
                break;

            case 'cost':
                const costValue = Number(meal.cost);
                if (comparisonRange === 'similar') {
                    // Use smarter range calculation: ±20% but cap at ±$2 for expensive meals
                    const percentageRange = costValue * 0.2;
                    const maxRange = 2;
                    const range = Math.min(percentageRange, maxRange);
                    filteredMeals = filteredMeals.filter(m => {
                        const mealCost = Number(m.cost);
                        return mealCost >= costValue - range && mealCost <= costValue + range;
                    });
                } else if (comparisonRange === 'lower') {
                    filteredMeals = filteredMeals.filter(m => {
                        const mealCost = Number(m.cost);
                        return mealCost < costValue;
                    });
                } else if (comparisonRange === 'higher') {
                    filteredMeals = filteredMeals.filter(m => {
                        const mealCost = Number(m.cost);
                        return mealCost > costValue;
                    });
                }
                break;

            case 'cuisine':
                if (meal.cuisine) {
                    filteredMeals = filteredMeals.filter(m => m.cuisine === meal.cuisine);
                }
                break;

            default:
                break;
        }

        // Debug logging
        if (comparisonType === 'calories' && comparisonRange === 'similar' && meal) {
            const calorieValue = Number(meal.total_calories);
            let range;
            if (calorieValue > 300) {
                const percentageRange = calorieValue * 0.1;
                const maxRange = 50;
                range = Math.min(percentageRange, maxRange);
            } else {
                const percentageRange = calorieValue * 0.2;
                const maxRange = 50;
                range = Math.min(percentageRange, maxRange);
            }
            console.log(`Comparing ${meal.name} (${calorieValue.toFixed(1)} cal) with range ±${range.toFixed(1)} = ${(calorieValue - range).toFixed(1)}-${(calorieValue + range).toFixed(1)} calories`);
            console.log(`Found ${filteredMeals.length} meals`);
            if (filteredMeals.length > 0) {
                console.log('Found meals:', filteredMeals.slice(0, 3).map(m => `${m.name} (${Number(m.total_calories).toFixed(1)} cal)`));
            }
        }

        setComparisonMeals(filteredMeals);
    }, [meal, comparisonType, comparisonRange]);

    if (loading) {
        return (
            <div className="meal-detail-loading">
                <div className="loading-spinner"></div>
                <p>Loading meal details...</p>
            </div>
        );
    }

    if (!meal) {
        return (
            <div className="meal-detail-error">
                <h2>Meal Not Found</h2>
                <p>The meal you're looking for doesn't exist.</p>
                <Link to="/explore" className="back-button">Back to Explore</Link>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{meal.name} - Recipe & Nutrition | Calorie Tracker</title>
                <meta name="description" content={`Discover ${meal.name} - a delicious recipe with ${meal.total_calories} calories and ${meal.total_protein}g protein. Serves ${meal.serving_size} people. Get the full recipe, ingredients, and cooking instructions in our mobile app.`} />
                <meta name="keywords" content={`${meal.name}, recipe, food, cooking, nutrition, calorie tracker, macro tracker, ${meal.total_calories} calories`} />
                <link rel="canonical" href={`https://prepwize.ai${generateMealUrl(meal.name, meal.meal_id)}`} />

                {/* Open Graph tags */}
                <meta property="og:title" content={`${meal.name} - Recipe & Nutrition`} />
                <meta property="og:description" content={`Discover ${meal.name} - a delicious recipe with ${meal.total_calories} calories and ${meal.total_protein}g protein. Serves ${meal.serving_size} people.`} />
                <meta property="og:image" content={meal.image} />
                <meta property="og:url" content={`https://prepwize.ai${generateMealUrl(meal.name, meal.meal_id)}`} />
                <meta property="og:type" content="article" />

                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${meal.name} - Recipe & Nutrition`} />
                <meta name="twitter:description" content={`Discover ${meal.name} - a delicious recipe with ${meal.total_calories} calories and ${meal.total_protein}g protein. Serves ${meal.serving_size} people.`} />
                <meta name="twitter:image" content={meal.image} />

                {/* Recipe Schema Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Recipe",
                        "name": meal.name,
                        "description": `Discover ${meal.name} - a delicious recipe with ${meal.total_calories} calories and ${meal.total_protein}g protein. Serves ${meal.serving_size} people.`,
                        "image": meal.image,
                        "url": `https://prepwize.ai${generateMealUrl(meal.name, meal.meal_id)}`,
                        "author": {
                            "@type": "Organization",
                            "name": "PrepWize"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "PrepWize",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://prepwize.ai/logo512.png"
                            }
                        },
                        "nutrition": {
                            "@type": "NutritionInformation",
                            "calories": `${meal.total_calories} calories`,
                            "proteinContent": `${meal.total_protein}g`,
                            "carbohydrateContent": `${meal.total_carb}g`,
                            "fiberContent": `${meal.total_fiber}g`
                        },
                        "recipeCategory": meal.cuisine || "Main Course",
                        "recipeCuisine": meal.cuisine,
                        "cookTime": "PT30M",
                        "prepTime": "PT15M",
                        "totalTime": "PT45M",
                        "recipeYield": `${meal.serving_size} servings`,
                        "keywords": `${meal.name}, recipe, ${meal.cuisine || 'food'}, nutrition, calorie tracker, macro tracker`,
                        "datePublished": "2024-01-01",
                        "dateModified": new Date().toISOString().split('T')[0]
                    })}
                </script>
            </Helmet>

            <div className="meal-detail-container">
                <div className="meal-detail-header">
                    <Link to="/explore" className="back-link">
                        ← Back to Explore
                    </Link>
                    <div className="app-info-banner">
                        <span className="app-info-icon">📱</span>
                        <span className="app-info-text">Get complete recipe details, step-by-step instructions, and full nutritional breakdown in the PrepWize mobile app</span>
                    </div>
                </div>

                <div className="meal-detail-content">
                    <div className="meal-detail-main">
                        <div className="meal-image-section">
                            <img src={meal.image} alt={meal.name} className="meal-detail-image" />
                        </div>

                        <div className="meal-info-section">
                            <div className="meal-header">
                                <h1 className="meal-title">{meal.name}</h1>
                                {meal.cuisine && (
                                    <div className="cuisine-badge">
                                        <span className="cuisine-icon">🌍</span>
                                        <span className="cuisine-text">{meal.cuisine}</span>
                                    </div>
                                )}
                            </div>

                            <div className="meal-nutrition-grid">
                                <div className="nutrition-card">
                                    <div className="nutrition-icon">🔥</div>
                                    <div className="nutrition-content">
                                        <span className="nutrition-label">Calories</span>
                                        <span className="nutrition-value">{meal.total_calories}</span>
                                    </div>
                                </div>

                                <div className="nutrition-card">
                                    <div className="nutrition-icon">💪</div>
                                    <div className="nutrition-content">
                                        <span className="nutrition-label">Protein</span>
                                        <span className="nutrition-value">{meal.total_protein}g</span>
                                    </div>
                                </div>

                                <div className="nutrition-card">
                                    <div className="nutrition-icon">🍞</div>
                                    <div className="nutrition-content">
                                        <span className="nutrition-label">Carbs</span>
                                        <span className="nutrition-value">{meal.total_carb}g</span>
                                    </div>
                                </div>

                                <div className="nutrition-card">
                                    <div className="nutrition-icon">🌾</div>
                                    <div className="nutrition-content">
                                        <span className="nutrition-label">Fiber</span>
                                        <span className="nutrition-value">{meal.total_fiber}g</span>
                                    </div>
                                </div>

                                <div className="nutrition-card">
                                    <div className="nutrition-icon">💰</div>
                                    <div className="nutrition-content">
                                        <span className="nutrition-label">Cost</span>
                                        <span className="nutrition-value">${meal.cost}</span>
                                    </div>
                                </div>

                                <div className="nutrition-card">
                                    <div className="nutrition-icon">🍽️</div>
                                    <div className="nutrition-content">
                                        <span className="nutrition-label">Serving Size</span>
                                        <span className="nutrition-value">{meal.serving_size}</span>
                                        <span className="nutrition-per-serving">servings</span>
                                    </div>
                                </div>
                            </div>

                            <div className="meal-comparison-section">
                                <h3>Compare This Meal</h3>
                                <div className="comparison-controls">
                                    <div className="comparison-filter">
                                        <label htmlFor="comparisonType">Compare by:</label>
                                        <select
                                            id="comparisonType"
                                            value={comparisonType}
                                            onChange={(e) => setComparisonType(e.target.value)}
                                        >
                                            <option value="calories">Calories</option>
                                            <option value="protein">Protein</option>
                                            <option value="cost">Cost</option>
                                            <option value="cuisine">Cuisine</option>
                                        </select>
                                    </div>
                                    {comparisonType !== 'cuisine' && (
                                        <div className="comparison-range">
                                            <label htmlFor="comparisonRange">Range:</label>
                                            <select
                                                id="comparisonRange"
                                                value={comparisonRange}
                                                onChange={(e) => setComparisonRange(e.target.value)}
                                            >
                                                <option value="similar">Similar (Smart Range)</option>
                                                <option value="lower">Lower</option>
                                                <option value="higher">Higher</option>
                                            </select>
                                        </div>
                                    )}
                                </div>

                                <div className="comparison-results">
                                    {comparisonMeals.length > 0 ? (
                                        <div className="comparison-grid">
                                            {comparisonMeals.slice(0, 3).map((compareMeal) => (
                                                <div key={compareMeal.meal_id} className="comparison-card">
                                                    <img src={compareMeal.image} alt={compareMeal.name} />
                                                    <div className="comparison-info">
                                                        <h4>{compareMeal.name}</h4>
                                                        <div className="comparison-stats">
                                                            <span className="stat-item">
                                                                <span className="stat-icon">🔥</span>
                                                                {compareMeal.total_calories} cal
                                                            </span>
                                                            <span className="stat-item">
                                                                <span className="stat-icon">💪</span>
                                                                {compareMeal.total_protein}g
                                                            </span>
                                                            <span className="stat-item">
                                                                <span className="stat-icon">💰</span>
                                                                ${compareMeal.cost}
                                                            </span>
                                                        </div>
                                                        {compareMeal.cuisine && (
                                                            <span className="comparison-cuisine">{compareMeal.cuisine}</span>
                                                        )}
                                                    </div>
                                                    <Link
                                                        to={generateMealUrl(compareMeal.name, compareMeal.meal_id)}
                                                        className="view-meal-btn"
                                                    >
                                                        View Meal
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-comparison">
                                            <p>No meals found matching your criteria. Try adjusting the filters above.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="app-cta-section">
                                <div className="cta-content">
                                    <h3>Get the Complete Recipe!</h3>
                                    <p>
                                        Download our mobile app to access the full recipe, including:
                                    </p>
                                    <ul className="recipe-features">
                                        <li>📝 Complete ingredient list with measurements</li>
                                        <li>👨‍🍳 Step-by-step cooking instructions</li>
                                        <li>⏱️ Prep and cooking time</li>
                                        <li>🍽️ Serving suggestions and tips</li>
                                        <li>📊 Detailed nutritional breakdown</li>
                                        <li>🔄 Recipe variations and substitutions</li>
                                    </ul>

                                    <div className="cta-buttons">
                                        <a href="https://apps.apple.com/us/app/prepwize-ai-meal-planner/id6744729394" target="_blank" rel="noopener noreferrer" className="cta-button primary">
                                            Download PrepWize Now
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {relatedMeals.length > 0 && (
                        <div className="related-meals-section">
                            <h2>Explore More Meals</h2>
                            <div className="related-meals-grid">
                                {relatedMeals.map((relatedMeal) => (
                                    <Link
                                        to={generateMealUrl(relatedMeal.name, relatedMeal.meal_id)}
                                        key={relatedMeal.meal_id}
                                        className="related-meal-card"
                                    >
                                        <img src={relatedMeal.image} alt={relatedMeal.name} />
                                        <div className="related-meal-info">
                                            <h4>{relatedMeal.name}</h4>
                                            <div className="related-meal-stats">
                                                <span>{relatedMeal.total_calories} cal</span>
                                                <span>{relatedMeal.total_protein}g protein</span>
                                                {relatedMeal.cuisine && (
                                                    <span className="related-meal-cuisine">{relatedMeal.cuisine}</span>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default MealDetail;
